import React, { useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import bailImage from "../../assets/mig2.jpg";
import contactImage from "../../assets/mig1.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

export default function StorySection() {
  const prefersReducedMotion = useReducedMotion();
  const tlRef = useRef(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0, opacity: 0 });

  const onTimelineMove = (e) => {
    const rect = tlRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCursor({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      opacity: prefersReducedMotion ? 0 : 1,
    });
  };

  const onTimelineLeave = () => setCursor((c) => ({ ...c, opacity: 0 }));

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left: Images + Quote */}
          <motion.div className="lg:col-span-5" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <div className="grid grid-cols-3 gap-3">
              <motion.div className="col-span-2" variants={fadeUp}>
                <div className="relative rounded-2xl overflow-hidden ring-1 ring-gray-200 shadow-md">
                  <img src={bailImage} alt="Professional service" className="h-64 w-full object-cover" />
                </div>
              </motion.div>
              <div className="col-span-1 flex flex-col gap-3">
                <div className="relative rounded-xl overflow-hidden ring-1 ring-gray-200 shadow-sm">
                  <img src={contactImage} alt="Office" className="h-32 w-full object-cover" />
                </div>
                <div className="relative rounded-xl overflow-hidden ring-1 ring-gray-200 shadow-sm">
                  <img src={bailImage} alt="Clients" className="h-32 w-full object-cover" />
                </div>
              </div>
            </div>

            <motion.div
              variants={fadeUp}
              className="mt-6 rounded-2xl border border-blue-200/60 bg-gradient-to-br from-blue-50 to-cyan-50 p-6 shadow-sm"
            >
              <p className="text-gray-700 leading-relaxed">
                “We started in 2008 with one promise: make immigration assistance{" "}
                <span className="font-semibold text-blue-700">humane, clear, and accessible</span> for every family.”
              </p>
              <div className="mt-3 text-sm text-blue-700/80 font-medium">
                — Citizen Hub Immigration Services
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Timeline */}
          <motion.div className="lg:col-span-7" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <motion.h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-gray-900" variants={fadeUp}>
              Our Story: Serving American Families Since 2008
            </motion.h2>
            <motion.p className="mt-4 text-lg text-gray-600" variants={fadeUp}>
              From a small, mission-driven office to a nationwide platform trusted by thousands — we’ve kept the same values: clarity, care, and speed.
            </motion.p>

            <div ref={tlRef} onMouseMove={onTimelineMove} onMouseLeave={onTimelineLeave} className="relative mt-8">
              <div
                className="pointer-events-none absolute -inset-px rounded-2xl transition-opacity duration-300"
                style={{
                  background: `radial-gradient(180px circle at ${cursor.x}px ${cursor.y}px, rgba(59,130,246,0.14), rgba(56,189,248,0.08) 35%, transparent 70%)`,
                  opacity: cursor.opacity,
                  filter: "blur(8px)",
                }}
              />
              <ol className="relative border-s-2 border-dashed border-blue-200 space-y-8 ps-6">
                {[
                  { year: "2008", title: "Where it began", desc: "Launched to simplify immigration forms and guidance with dignity and respect." },
                  { year: "2014", title: "Network expansion", desc: "Grew a vetted, nationwide lawyer network and standardized transparent pricing." },
                  { year: "2019", title: "Modern platform", desc: "Introduced secure dashboards, case tracking, and flexible payment options." },
                  { year: "Today", title: "Trusted at scale", desc: "24/7 support with rapid form processing and dedicated specialists." },
                ].map((step, i) => (
                  <motion.li key={i} className="relative" variants={fadeUp}>
                    <span className="absolute -start-[13px] top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white ring-2 ring-blue-500 text-blue-600 text-xs font-bold">
                      {i + 1}
                    </span>
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-200">
                          {step.year}
                        </span>
                        <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                      </div>
                      <p className="mt-3 text-gray-600">{step.desc}</p>
                    </div>
                  </motion.li>
                ))}
              </ol>
            </div>

            <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-gray-200 bg-white px-5 py-3 shadow-sm">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white">
                <svg className="h-4.5 w-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.95.68l1.5 4.5a1 1 0 01-.5 1.2l-2.26 1.13a11.04 11.04 0 005.52 5.52l1.13-2.26a1 1 0 011.2-.5l4.5 1.5c.4.13.68.5.68.95V19a2 2 0 01-2 2h-1C9.72 21 3 14.28 3 6V5z" />
                </svg>
              </span>
              <div className="text-sm">
                <div className="text-gray-500">Immigration Support (24/7)</div>
                <div className="text-gray-900 font-semibold text-lg">+1 3108802133</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}